import moment from "moment";

import types from "./types";
import { firestore, ORG_COLL } from "_firebase";
import userActions from "store/users/actions";

const getUserOrg = (orgId) => firestore.doc(`${ORG_COLL}/${orgId}`).get();

const getOrgChbaWorkbookTemplates = (orgId) =>
    firestore.doc(`${ORG_COLL}/${orgId}/chbaWorkbook/checklistTemplates`).get();

// The organization's shared model tree. Same shape as the personal directory.
const getOrgDir = (orgId) => firestore.doc(`${ORG_COLL}/${orgId}`).collection("modelDir").doc("directory").get();

// Organizations created before the shared folder existed have no tree document at all, and the
// folder thunks write with update(), which fails on a document that is not there. Seeding it on
// first read makes an old organization behave like one created today. Idempotent: it runs once.
const createOrgDir = (orgId) =>
    firestore.doc(`${ORG_COLL}/${orgId}`).collection("modelDir").doc("directory").set({ folders: {}, models: {} });

const setUserOrg = (orgId, organizationData) => ({
    type: types.GET_USER_ORG,
    orgId,
    organizationData,
});

const toggleOrganizationLoading = (orgLoading) => ({
    type: types.TOGGLE_ORG_LOADING,
    orgLoading,
});

const setOrgDir = (orgId, modelDir) => ({
    type: types.GET_ORG_DIR,
    orgId,
    modelDir,
});

// The organization's code library. Unlike the personal one, which packs every code for a component
// type into one document's `codes` map and overflows into subcollections, this mirrors upgradesLib:
// one document per code, keyed by its ref. No map to outgrow, and writes touch one code at a time.
const getOrgCodeLibDocs = (orgId) => firestore.doc(`${ORG_COLL}/${orgId}`).collection("codeLib").get();

const orgCodeRefDoc = (orgId, codeRef) => firestore.doc(`${ORG_COLL}/${orgId}`).collection("codeLib").doc(codeRef);

const setOrgCodeLib = (orgId, codeLib) => ({
    type: types.GET_ORG_CODE_LIB,
    orgId,
    codeLib,
});

const setOrgCode = (orgId, codeRef, code) => ({
    type: types.SET_ORG_CODE,
    orgId,
    codeRef,
    code,
});

const removeOrgCode = (orgId, codeRef) => ({
    type: types.DELETE_ORG_CODE,
    orgId,
    codeRef,
});

const fetchOrgCodeLib = (orgId) => (dispatch) => {
    if (!orgId) return null;

    return getOrgCodeLibDocs(orgId)
        .then((snapshot) =>
            dispatch(
                setOrgCodeLib(
                    orgId,
                    snapshot.docs.reduce((acc, doc) => ({ ...acc, [doc.id]: { ...doc.data(), codeRef: doc.id } }), {})
                )
            )
        )
        .catch((err) => {
            console.error(`Error fetching code library for organization ${orgId}:`, err.message);

            return dispatch(userActions.userFail("There was a problem fetching the organization code library"));
        });
};

// Promoting a code copies it: the personal one stays where it is, as upgrade packages do.
const saveCodeToOrgLib =
    ({ orgId, codeRef, code, uid }) =>
    async (dispatch) => {
        if (!orgId || !codeRef) return null;

        const orgCode = { ...code, codeRef, lastEdited: moment().format("LLLL"), lastEditedBy: uid };

        try {
            await orgCodeRefDoc(orgId, codeRef).set(orgCode);

            return dispatch(setOrgCode(orgId, codeRef, orgCode));
        } catch (err) {
            console.error(`Error saving code ${codeRef} to organization ${orgId}:`, err.message);

            return dispatch(userActions.userFail("There was a problem saving the code to the organization library"));
        }
    };

const deleteCodeFromOrgLib =
    ({ orgId, codeRef }) =>
    async (dispatch) => {
        if (!orgId || !codeRef) return null;

        try {
            await orgCodeRefDoc(orgId, codeRef).delete();

            return dispatch(removeOrgCode(orgId, codeRef));
        } catch (err) {
            console.error(`Error deleting code ${codeRef} from organization ${orgId}:`, err.message);

            return dispatch(
                userActions.userFail("There was a problem deleting the code from the organization library")
            );
        }
    };

const setOrgDirError = (orgId, orgDirError) => ({
    type: types.SET_ORG_DIR_ERROR,
    orgId,
    orgDirError,
});

const toggleOrgDirLoading = (orgDirLoading) => ({
    type: types.TOGGLE_ORG_DIR_LOADING,
    orgDirLoading,
});

const updateOrganizationStart = () => ({
    type: types.UPDATE_ORGANIZATION_START,
});

const updateOrganizationSuccess = (data) => ({
    type: types.UPDATE_ORGANIZATION_SUCCESS,
    payload: data,
});

const updateOrganizationFail = (error) => ({
    type: types.UPDATE_ORGANIZATION_FAIL,
    payload: error,
});

const updateOrganizationChbaWorkbookTemplateSuccess = (data) => ({
    type: types.UPDATE_ORGANIZATION_CHBA_WORKBOOK_TEMPLATE_SUCCESS,
    payload: data,
});

const updateOrganizationChbaWorkbookTemplateFail = (error) => ({
    type: types.UPDATE_ORGANIZATION_CHBA_WORKBOOK_TEMPLATE_FAIL,
    payload: error,
});

const fetchUserOrganization = (uid, orgId) => (dispatch) => {
    if (!uid) return null;
    if (!orgId) return null;

    dispatch(toggleOrganizationLoading(true));
    return getUserOrg(orgId)
        .then((data) => dispatch(setUserOrg(orgId, data.data() || {})))
        .then(() => getOrgChbaWorkbookTemplates(orgId))
        .then((data) =>
            dispatch(updateOrganizationChbaWorkbookTemplateSuccess({ orgId, chbaWorkbookTemplates: data.data() || {} }))
        )
        .then((data) => dispatch(toggleOrganizationLoading(false)))
        .catch((err) => {
            console.error(`Error fetching organization ${orgId}:`, err.message);

            // A failed attempt still has to leave an entry behind. Callers fetch while
            // `details` is empty, so bailing out without one asks them to try again forever.
            dispatch(setUserOrg(orgId, {}));
            dispatch(toggleOrganizationLoading(false));
            dispatch(userActions.userFail("There was a problem fetching user's organization information"));
        });
};

const fetchOrgDir =
    (orgId, skipLoading = false) =>
    (dispatch) => {
        if (!orgId) return null;

        if (!skipLoading) dispatch(toggleOrgDirLoading(true));

        return getOrgDir(orgId)
            .then(async (data) => {
                if (!data.exists) {
                    await createOrgDir(orgId);

                    return dispatch(setOrgDir(orgId, { folders: {}, models: {} }));
                }

                return dispatch(setOrgDir(orgId, data.data() || {}));
            })
            .then(() => dispatch(setOrgDirError(orgId, false)))
            .then(() => dispatch(toggleOrgDirLoading(false)))
            .catch((err) => {
                console.error(`Error fetching shared folder for organization ${orgId}:`, err.message);

                // the shared folder must not read as "empty" when it could not be read at all
                dispatch(setOrgDirError(orgId, true));

                return dispatch(toggleOrgDirLoading(false));
            });
    };

const updateOrganization = (orgId, updates) => async (dispatch) => {
    try {
        dispatch(updateOrganizationStart());

        await firestore.doc(`${ORG_COLL}/${orgId}`).set({ ...updates }, { merge: true });

        const orgData = (await firestore.doc(`${ORG_COLL}/${orgId}`).get()).data();

        dispatch(updateOrganizationSuccess({ orgId, organizationData: orgData }));
    } catch (error) {
        dispatch(updateOrganizationFail(error));
    }
};

const updateOrganizationChbaWorkbookTemplate =
    (uid, orgId, templateId, templateType, templateName, templateData) => async (dispatch) => {
        try {
            dispatch({ type: types.UPDATE_ORGANIZATION_CHBA_WORKBOOK_TEMPLATE_START });

            if (templateType && templateId && templateName && templateData) {
                await firestore.doc(`${ORG_COLL}/${orgId}/chbaWorkbook/checklistTemplates`).set(
                    {
                        [templateType]: {
                            [templateId]: {
                                name: templateName,
                                data: templateData,
                            },
                        },
                    },
                    { merge: true }
                );
            }

            const chbaWorkbookTemplates = (
                await firestore.doc(`${ORG_COLL}/${orgId}/chbaWorkbook/checklistTemplates`).get()
            ).data();

            dispatch(
                updateOrganizationChbaWorkbookTemplateSuccess({
                    orgId,
                    chbaWorkbookTemplates,
                })
            );
        } catch (error) {
            dispatch(updateOrganizationChbaWorkbookTemplateFail(error));
        }
    };

export default {
    fetchUserOrganization,
    fetchOrgDir,
    fetchOrgCodeLib,
    saveCodeToOrgLib,
    deleteCodeFromOrgLib,
    updateOrganization,
    updateOrganizationChbaWorkbookTemplate,
};
